/* eslint-disable @typescript-eslint/no-explicit-any */
import moment from "moment-timezone";
import React, { useState, useMemo } from "react";
import { Box, Typography, Button, Chip, TextField, MenuItem } from "@mui/material";

import List from "../../../components/List";
import { UserMode } from "../../../interfaces/User";
import { useAttendancePunches } from "../../../hooks/query/useAttendancePunches";
import { AttendancePunchCreateEdit } from "./AttendancePunchCreateEdit";
import AttendancePunchInfo from "./AttendancePunchInfo";
import { IAttendancePunch } from "../../../interfaces/AttendanceInterface";
import { AttendanceDutyType, AttendancePunchType } from "../../../constants/attendance";
import EmployeeAndSpSelect from "../../../components/Employee/EmployeeAndSpSelect";
import { convertADtoBS } from "../../../components/Calendar/functions/calendarFunctions";
import CalendarDropdown from "../../../components/CalendarDropdown/CalendarDropdown";
import { AttendanceSettingsPanel } from "../../ResourceCentre/ModuleSettings/AttendanceSettings/AttendanceSettingsPanel";
import useIsUserRcAdmin from "../../../hooks/useIsUserRcAdmin";
import { DEFAULT_TIMEZONE } from "../../../constants/resourceCentre";
import { EmptyView } from "../../../components/OList";

function getDutyTypeChipColor(dutyType?: string): "warning" | "info" | "default" {
  if (dutyType === AttendanceDutyType.OVERTIME) return "warning";
  if (dutyType === AttendanceDutyType.ON_CALL) return "info";
  return "default";
}

// eslint-disable-next-line import/prefer-default-export
export function AttendancePunchesList(): React.JSX.Element {
  const isAdmin = useIsUserRcAdmin();

  const [filters, setFilters] = useState<{
    employeeIds: number[];
    serviceProviderIds: number[];
    date: Date | null;
    staff: any | null;
    dutyType: string;
  }>({
    employeeIds: [],
    serviceProviderIds: [],
    date: new Date(),
    staff: null,
    dutyType: ""
  });

  const queryParams = useMemo(() => {
    const params: any = {};
    if (filters.date) {
      const formattedDate = moment(filters.date).format("YYYY-MM-DD");
      params.fromDate = formattedDate;
      params.toDate = formattedDate;
    }
    if (filters.employeeIds.length > 0) params.employeeIds = filters.employeeIds;
    if (filters.serviceProviderIds.length > 0)
      params.serviceProviderIds = filters.serviceProviderIds;
    if (filters.dutyType) params.dutyType = filters.dutyType;
    return params;
  }, [filters]);

  const { data: punches = [], isLoading } = useAttendancePunches(queryParams);

  const [showCreateEditPanel, setShowCreateEditPanel] = useState(false);
  const [selectedPunch, setSelectedPunch] = useState<IAttendancePunch | null>(null);

  const handleSuccess = () => {
    // to close the panel after edit or creation, set to null
    setSelectedPunch(null);
    setShowCreateEditPanel(false);
  };

  return (
    <Box sx={{ height: "100%", pt: 2 }}>
      <List<IAttendancePunch>
        withoutSearch
        automation="attendancePunchesList"
        data={punches}
        isLoading={isLoading}
        isResponsive
        activeRow={selectedPunch?.id}
        onRowClick={(row) => {
          setSelectedPunch(selectedPunch?.id === row.id ? null : (row as IAttendancePunch));
        }}
        hideCreateButton
        additionalHeaderFilters={
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
              mt: { xs: 2, md: 0 }
            }}
          >
            <CalendarDropdown
              allowNull
              width="150px"
              label="Punch Date"
              placeholder="Select Date"
              date={filters.date || undefined}
              onChange={(date) => setFilters({ ...filters, date })}
              TextFieldProps={{
                variant: "outlined",
                size: "small",
                InputLabelProps: { shrink: true }
              }}
            />

            <TextField
              select
              variant="outlined"
              size="small"
              value={filters.dutyType || "all"}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  dutyType: e.target.value === "all" ? "" : e.target.value
                })
              }
              sx={{ width: 140 }}
              label="Duty Type"
              InputLabelProps={{ shrink: true }}
              slotProps={{ inputLabel: { shrink: true } }}
            >
              <MenuItem value="all">All Duties</MenuItem>
              {Object.entries(AttendanceDutyType).map(([key, value]) => (
                <MenuItem key={value} value={value} sx={{ textTransform: "capitalize" }}>
                  {key.toLowerCase().replace(/_/g, " ")}
                </MenuItem>
              ))}
            </TextField>

            <EmployeeAndSpSelect
              selectedEmployee={filters.staff}
              onChange={(emp) => {
                const newFilters: any = {
                  ...filters,
                  staff: emp,
                  employeeIds: [],
                  serviceProviderIds: []
                };

                if (emp) {
                  const isEmployee =
                    emp.userAuthenticable?.[0]?.authenticable === UserMode.Employee ||
                    Boolean(emp && "nonUserEmployee" in emp && emp.nonUserEmployee);
                  const isServiceProvider =
                    emp.userAuthenticable?.[0]?.authenticable === UserMode.ServiceProvider;

                  if (isEmployee) {
                    newFilters.employeeIds = [emp.id];
                  } else if (isServiceProvider) {
                    newFilters.serviceProviderIds = [emp.id];
                  }
                }
                setFilters(newFilters);
              }}
              sx={{ width: 200 }}
              TextFieldProps={{
                size: "small",
                placeholder: "Select staff...",
                label: "Select staff..."
              }}
            />

            <Button
              variant="contained"
              sx={{ height: 35 }}
              onClick={() => {
                setShowCreateEditPanel(true);
              }}
            >
              Create Punch
            </Button>

            {isAdmin && <AttendanceSettingsPanel />}
          </Box>
        }
        columns={[
          {
            key: "id",
            label: "ID",
            formatter: (row: IAttendancePunch) => (
              <Typography variant="body2" color="text.secondary">
                #{row.id}
              </Typography>
            )
          },
          {
            key: "name",
            label: "Name",
            sortable: true,
            formatter: (row: IAttendancePunch) => {
              const user = row.employee || row.serviceProvider;
              const name = user ? `${user.firstName || ""} ${user.lastName || ""}` : "Unknown";
              return (
                <Typography variant="body2" sx={{ fontWeight: 500, textTransform: "capitalize" }}>
                  {name}
                </Typography>
              );
            }
          },
          {
            key: "type",
            label: "User Type",
            formatter: (row: IAttendancePunch) => (
              <Typography variant="body2">
                {row.employeeId ? "Employee" : "Service Provider"}
              </Typography>
            )
          },
          {
            key: "punchDate",
            label: "Punch Date",
            sortable: true,
            formatter: (row: IAttendancePunch) => (
              <Typography variant="body2">
                {convertADtoBS(moment.tz(row.punchedAt, DEFAULT_TIMEZONE).toDate()).formatted2}
              </Typography>
            )
          },
          {
            key: "punchTime",
            label: "Punch Time",
            sortable: true,
            formatter: (row: IAttendancePunch) => (
              <Typography variant="caption" color="text.secondary">
                {moment.tz(row.punchedAt, DEFAULT_TIMEZONE).format("hh:mm A")}
              </Typography>
            )
          },
          {
            key: "punchType",
            label: "Type",
            formatter: (row: IAttendancePunch) => (
              <Typography
                variant="body2"
                sx={{
                  textTransform: "uppercase",
                  color:
                    row.punchType.toLowerCase() === AttendancePunchType.CHECK_IN
                      ? "success.main"
                      : "error.main",
                  fontWeight: 600
                }}
              >
                {row.punchType}
              </Typography>
            )
          },
          {
            key: "dutyType",
            label: "Duty Type",
            formatter: (row: IAttendancePunch) => {
              const duty = row.dutyType || AttendanceDutyType.REGULAR;
              return (
                <Chip
                  label={duty.replace(/_/g, " ")}
                  size="small"
                  variant="outlined"
                  color={getDutyTypeChipColor(row.dutyType)}
                  sx={{
                    textTransform: "capitalize",
                    fontWeight: 500
                  }}
                />
              );
            }
          },
          {
            key: "punchSource",
            label: "Source",
            formatter: (row: IAttendancePunch) => (
              <Typography variant="body2" sx={{ textTransform: "uppercase" }}>
                {row.punchSource}
              </Typography>
            )
          }
        ]}
      >
        <EmptyView>
          <Box textAlign="center" padding="50px">
            No items to show.
          </Box>
        </EmptyView>
      </List>

      {/* panels */}
      {selectedPunch && (
        <AttendancePunchInfo
          punch={selectedPunch}
          onClose={() => setSelectedPunch(null)}
          onEdit={() => setShowCreateEditPanel(true)}
        />
      )}

      {showCreateEditPanel && (
        <AttendancePunchCreateEdit
          onClose={() => {
            setShowCreateEditPanel(false);
            setSelectedPunch(null);
          }}
          punch={selectedPunch}
          onSuccess={handleSuccess}
        />
      )}
    </Box>
  );
}
